import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme, fonts } from "@/theme";
import { Icon } from "@/components/Icon";
import { useProgress } from "@/context/ProgressContext";
import { ALL_MODULES, MODULES, TOTAL_LESSONS } from "@/data/modules";
import { LessonChecklist, LessonQuiz } from "@/components/LessonBlocks";
import { Screen } from "@/components/ui";

export default function LessonPage() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const { isDone, toggle, countDone } = useProgress();
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizPassed, setQuizPassed] = useState(false);

  let lesson;
  for (const m of ALL_MODULES) {
    const found = m.lessons.find((l) => l.id === id);
    if (found) { lesson = found; break; }
  }

  const back = (
    <button data-testid="lesson-back-button" className="pressable" onClick={() => navigate(-1)} style={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 12, marginLeft: -4, background: "none", border: "none", cursor: "pointer" }}>
      <Icon name="chevron-left" size={20} color={colors.brand} />
      <span style={{ color: colors.brand, fontSize: 15, fontWeight: 700, fontFamily: fonts.text }}>Back</span>
    </button>
  );

  if (!lesson) {
    return (
      <Screen>
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${colors.divider}` }}>{back}</div>
        <div style={{ padding: 40, textAlign: "center", color: colors.muted, fontFamily: fonts.text }}>This lesson could not be found.</div>
      </Screen>
    );
  }

  const complete = isDone(lesson.id);
  const doneTotal = MODULES.reduce((n, m) => n + countDone(m.lessons.map((l) => l.id)), 0);
  const allComplete = doneTotal === TOTAL_LESSONS;
  const showCompletion = lesson.kind === "quiz" && quizPassed && allComplete;

  return (
    <Screen>
      <div className="ritzy-fade" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${colors.divider}`, position: "sticky", top: 0, background: colors.surface, zIndex: 10 }}>
          {back}
          <div style={{ color: colors.onSurface, fontSize: 24, fontFamily: fonts.display, fontWeight: 700 }}>{lesson.title}</div>
        </div>

        <div data-testid="lesson-content-area" style={{ padding: "20px 24px", flex: 1 }}>
          {lesson.body?.map((block, i) =>
            block.type === "h" ? (
              <div key={i} style={{ color: colors.onSurface, fontSize: 18, fontFamily: fonts.display, fontWeight: 700, marginTop: 8, marginBottom: 12 }}>{block.text}</div>
            ) : (
              <p key={i} style={{ color: colors.onSurface, fontSize: 16, lineHeight: "25px", fontFamily: fonts.text, marginBottom: 16, whiteSpace: "pre-wrap" }}>{block.text}</p>
            ),
          )}

          {lesson.kind === "checklist" && lesson.checklist ? <LessonChecklist lessonId={lesson.id} items={lesson.checklist} /> : null}
          {lesson.kind === "quiz" && lesson.quiz ? (
            <LessonQuiz questions={lesson.quiz} answers={quizAnswers} onSelect={(qid, oi) => setQuizAnswers((prev) => ({ ...prev, [qid]: oi }))} onResult={setQuizPassed} />
          ) : null}

          {showCompletion ? (
            <div data-testid="training-complete-banner" style={{ marginTop: 24, background: colors.brand, borderRadius: 18, padding: "22px 18px", textAlign: "center" }}>
              <span style={{ color: colors.onBrand, fontSize: 18, fontFamily: fonts.display, fontWeight: 700 }}>You're officially ready, Weirdo 🖤</span>
            </div>
          ) : null}
        </div>

        <div style={{ padding: "12px 24px 24px", borderTop: `1px solid ${colors.divider}`, background: colors.surface, position: "sticky", bottom: 0 }}>
          <button data-testid="mark-lesson-complete-button" className="pressable" onClick={() => toggle(lesson.id)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: complete ? colors.success : colors.brandPrimary, border: "none", borderRadius: 16, padding: "16px", cursor: "pointer" }}>
            <Icon name={complete ? "check-circle" : "circle"} size={18} color={complete ? colors.onSuccess : colors.onBrandPrimary} />
            <span style={{ color: complete ? colors.onSuccess : colors.onBrandPrimary, fontSize: 16, fontWeight: 700, fontFamily: fonts.text }}>{complete ? "Lesson completed" : "Mark lesson complete"}</span>
          </button>
        </div>
      </div>
    </Screen>
  );
}
